/**
 * Centralized UI configuration for different client themes and settings
 * This allows easy customization per firm/client
 */

export interface ThemeConfig {
  // Colors
  primary: string;
  secondary: string;
  accent: string;
  destructive: string;
  muted: string;
  success: string;
  warning: string;
  
  // Button variants
  defaultButtonVariant: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  primaryButtonVariant: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  
  // Table settings
  tableStriped: boolean;
  tableHover: boolean;
  tableBordered: boolean;
  
  // Form settings
  formSpacing: 'compact' | 'normal' | 'spacious';
  inputSize: 'sm' | 'default' | 'lg';
  
  // Card settings
  cardShadow: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  cardBorder: boolean;
  
  // Layout
  maxWidth: string;
  sidebarWidth: string;
  headerHeight: string;
}

export interface MatterConfig {
  // Pagination
  defaultPageSize: number;
  pageSizeOptions: number[];
  
  // Rounding
  defaultRoundingType: 'nearest' | 'up' | 'down';
  defaultRoundingAmount: number;
  roundingAmountOptions: number[];
  
  // Export
  enablePdfExport: boolean;
  enableExcelExport: boolean;
  
  // Validation
  requireGroupName: boolean;
  maxBudget: number;
  minFeeSplit: number;
  maxFeeSplit: number;
  
  // Display
  showGroupNameInTitle: boolean;
  defaultShowInactive: boolean;
  enableBulkActions: boolean;
  
  // Archive behavior
  confirmArchive: boolean;
  confirmUnarchive: boolean;
  confirmDelete: boolean;
}

export interface FormConfig {
  // Grid layout
  defaultColumns: number;
  responsiveBreakpoints: {
    sm: number;
    md: number;
    lg: number;
  };
  
  // Field settings
  requiredFieldMarker: string;
  fieldSpacing: string;
  labelFontWeight: string;
  
  // Validation
  showValidationOnBlur: boolean;
  showValidationOnChange: boolean;
  
  // Submit behavior
  disableOnSubmit: boolean;
  showLoadingSpinner: boolean;
}

// Default configuration - can be overridden per client
export const defaultTheme: ThemeConfig = {
  primary: '#0f172a',
  secondary: '#64748b',
  accent: '#3b82f6',
  destructive: '#ef4444',
  muted: '#f1f5f9',
  success: '#10b981',
  warning: '#f59e0b',
  
  defaultButtonVariant: 'outline',
  primaryButtonVariant: 'outline',
  
  tableStriped: false,
  tableHover: true,
  tableBordered: true,
  
  formSpacing: 'normal',
  inputSize: 'default',
  
  cardShadow: 'sm',
  cardBorder: true,
  
  maxWidth: '7xl',
  sidebarWidth: '16rem',
  headerHeight: '4rem',
};

export const defaultMatterConfig: MatterConfig = {
  defaultPageSize: 100,
  pageSizeOptions: [25, 50, 100, 200],
  
  defaultRoundingType: 'nearest',
  defaultRoundingAmount: 0,
  roundingAmountOptions: [0, 0.1, 0.25, 0.5, 1, 5, 10, 15, 30],
  
  enablePdfExport: true,
  enableExcelExport: true,
  
  requireGroupName: false,
  maxBudget: 10000000,
  minFeeSplit: 0,
  maxFeeSplit: 100,
  
  showGroupNameInTitle: true,
  defaultShowInactive: false,
  enableBulkActions: false,
  
  confirmArchive: true,
  confirmUnarchive: true,
  confirmDelete: true,
};

export const defaultFormConfig: FormConfig = {
  defaultColumns: 2,
  responsiveBreakpoints: {
    sm: 1,
    md: 2,
    lg: 2,
  },
  
  requiredFieldMarker: '*',
  fieldSpacing: 'space-y-4',
  labelFontWeight: 'font-medium',
  
  showValidationOnBlur: true,
  showValidationOnChange: false,
  
  disableOnSubmit: true,
  showLoadingSpinner: true,
};

// Configuration per client/firm (example)
export const clientConfigs: Record<string, Partial<ThemeConfig & MatterConfig & FormConfig>> = {
  'firm-modern': {
    primary: '#3b82f6',
    accent: '#8b5cf6',
    cardShadow: 'lg',
    formSpacing: 'spacious',
    defaultPageSize: 50,
    enableBulkActions: true,
  },
  'firm-classic': {
    primary: '#1f2937',
    secondary: '#6b7280',
    cardShadow: 'none',
    tableBordered: true,
    formSpacing: 'compact',
    defaultPageSize: 25,
  },
  'firm-minimal': {
    primary: '#000000',
    cardShadow: 'none',
    cardBorder: false,
    tableStriped: true,
    formSpacing: 'normal',
  },
  // Example integration with clients module
  'law-firm-boutique': {
    primary: '#dc2626',
    accent: '#f59e0b',
    cardShadow: 'md',
    formSpacing: 'spacious',
    defaultPageSize: 10,
    // These will be picked up by the clients module configuration
  },
};

// Hook to get current configuration
export const useUIConfig = (firmId?: string) => {
  const firmConfig = firmId ? clientConfigs[firmId] || {} : {};
  
  return {
    theme: { ...defaultTheme, ...firmConfig },
    matter: { ...defaultMatterConfig, ...firmConfig },
    form: { ...defaultFormConfig, ...firmConfig },
  };
};
